import {
  type EntityVersionRouteIntent,
  type FeatureFlagKey,
  type FeatureFlagMap,
  type ProjectRouteKey,
} from "@repo/shared";
import type { ComponentType } from "react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { breadcrumb } from "@/lib/observability";
import { catalogHasEntityType, type ProjectFrameworkCatalog } from "../lib/framework-registry";

export function createProjectRouteViewLoaders(retryAttempt: number) {
  return {
    retryAttempt,
    ProjectHomePage: lazyWithRetry(() => import("../views/project-home/project-home-page")),
    ProjectDocumentsPage: lazyWithRetry(
      () => import("@/features/documents/views/project-documents")
    ),
    AiAssistantPage: lazyWithRetry(() => import("../views/ai-assistant")),
    ConversationsListPage: lazyWithRetry(() => import("../views/conversations-list")),
    SpecificationClarificationRequestsPage: lazyWithRetry(
      () => import("../views/specification-clarification-requests")
    ),
    KnowledgePage: lazyWithRetry(() => import("../views/knowledge")),
    SpecificationVersionSnapshotsPlaceholderPage: lazyWithRetry(
      () => import("../views/specification-version-snapshots")
    ),
    SpecificationQualityGuardPage: lazyWithRetry(
      () => import("../views/specification-quality-guard")
    ),
    ProjectQualityGuardConfigurationPage: lazyWithRetry(
      () => import("../views/project-quality-guard-configuration")
    ),
    FrameworkEntityPage: lazyWithRetry(() => import("../views/framework-entity")),
    FrameworkFolderPage: lazyWithRetry(() => import("../views/framework-folder")),
    ProjectPathNotFoundPage: lazyWithRetry(() => import("../views/project-path-not-found")),
  };
}

export type RouteComponents = ReturnType<typeof createProjectRouteViewLoaders>;

interface ResolveProjectRouteViewArgs {
  components: RouteComponents;
  routeKey: ProjectRouteKey;
  params: Record<string, string>;
  normalizedPath: string;
  compiledCatalog?: ProjectFrameworkCatalog | null;
  versionIntent?: EntityVersionRouteIntent;
  entityTabSlug?: string;
  baseEntityPath?: string;
  /** The caller's flags, and whether they have arrived. Both decide a gated route. */
  featureFlags: FeatureFlagMap;
  featureFlagStatus: "pending" | "ready";
}

interface MatchResult {
  kind: "view";
  Component: ComponentType<any>;
  params: Record<string, unknown>;
  remountKey: string;
}

/**
 * A gated route with no answer yet. The caller renders its loading skeleton and mounts
 * nothing, because a page behind a flag issues queries the backend would refuse.
 */
interface PendingResult {
  kind: "pending";
}

export type ProjectRouteViewResolution = MatchResult | PendingResult;

interface RouteViewContext {
  params: Record<string, string>;
  normalizedPath: string;
  compiledCatalog?: ProjectFrameworkCatalog | null;
  versionIntent?: EntityVersionRouteIntent;
  entityTabSlug?: string;
  baseEntityPath?: string;
}

interface RouteViewEntry {
  getComponent: (components: RouteComponents) => ComponentType<any>;
  mapParams: (context: RouteViewContext) => Record<string, unknown> | null;
  remountKey?: (context: RouteViewContext) => string;
  /**
   * The flag this route needs. Route parsing knows nothing about flags, so without this the
   * page behind a disabled feature would still resolve and issue queries the backend refuses.
   */
  featureFlag?: FeatureFlagKey;
}

const PROJECT_VIEW_REGISTRY: Record<ProjectRouteKey, RouteViewEntry> = {
  /**
   * The project root path `/` renders the Project Home screen (LAT-1004).
   *
   * Between LAT-870 and LAT-1004 `/` had no screen of its own and borrowed the Documents
   * page; the two keys are now separate views again. What never moved is the `welcome`
   * route key itself — it survives in `parseProjectPath`/`labels`, so every existing link
   * to `/`, including the post-create redirect, keeps working and keeps its identity.
   */
  welcome: {
    getComponent: (components) => components.ProjectHomePage,
    mapParams: () => ({}),
  },
  projectDocuments: {
    getComponent: (components) => components.ProjectDocumentsPage,
    mapParams: () => ({}),
  },
  aiAssistant: {
    getComponent: (components) => components.AiAssistantPage,
    mapParams: () => ({}),
  },
  aiAssistantNewChat: {
    getComponent: (components) => components.AiAssistantPage,
    mapParams: () => ({ newChat: "true" }),
  },
  aiAssistantHistory: {
    getComponent: (components) => components.ConversationsListPage,
    mapParams: () => ({}),
  },
  aiAssistantThread: {
    getComponent: (components) => components.AiAssistantPage,
    mapParams: ({ params }) => params,
  },
  specificationClarificationRequests: {
    getComponent: (components) => components.SpecificationClarificationRequestsPage,
    mapParams: () => ({}),
  },
  knowledge: {
    getComponent: (components) => components.KnowledgePage,
    mapParams: () => ({}),
  },
  specificationVersionSnapshots: {
    getComponent: (components) => components.SpecificationVersionSnapshotsPlaceholderPage,
    mapParams: ({ params }) => params,
    remountKey: () => "specificationVersionSnapshots",
    featureFlag: "version_snapshots",
  },
  specificationQualityGuard: {
    getComponent: (components) => components.SpecificationQualityGuardPage,
    mapParams: () => ({}),
  },
  projectQualityGuardConfiguration: {
    getComponent: (components) => components.ProjectQualityGuardConfigurationPage,
    mapParams: () => ({}),
  },
  folder: {
    getComponent: (components) => components.FrameworkFolderPage,
    mapParams: ({ params }) => params,
  },
  entity: {
    getComponent: (components) => components.FrameworkEntityPage,
    mapParams: ({
      params,
      normalizedPath,
      compiledCatalog,
      versionIntent,
      entityTabSlug,
      baseEntityPath,
    }) => {
      if (
        typeof params.entityTypeKey !== "string" ||
        !catalogHasEntityType(compiledCatalog, params.entityTypeKey)
      ) {
        return { path: normalizedPath, notFound: true };
      }
      if (!baseEntityPath) {
        return { path: normalizedPath, notFound: true };
      }
      return {
        ...params,
        baseEntityPath,
        ...(versionIntent ? { versionIntent } : {}),
        ...(entityTabSlug !== undefined ? { entityTabSlug } : {}),
      };
    },
    remountKey: ({ baseEntityPath, normalizedPath }) => baseEntityPath ?? normalizedPath,
  },
  notFound: {
    getComponent: (components) => components.ProjectPathNotFoundPage,
    mapParams: ({ normalizedPath }) => {
      breadcrumb(
        "routing.unknown_path",
        { path: normalizedPath, fallback: "not_found" },
        { category: "app" }
      );
      return { path: normalizedPath };
    },
  },
};

function notFoundResult(components: RouteComponents, normalizedPath: string): MatchResult {
  return {
    kind: "view",
    Component: components.ProjectPathNotFoundPage,
    params: { path: normalizedPath },
    remountKey: normalizedPath,
  };
}

export function resolveProjectRouteView({
  components,
  routeKey,
  params,
  normalizedPath,
  compiledCatalog,
  versionIntent,
  entityTabSlug,
  baseEntityPath,
  featureFlags,
  featureFlagStatus,
}: ResolveProjectRouteViewArgs): ProjectRouteViewResolution {
  const entry = PROJECT_VIEW_REGISTRY[routeKey];
  if (!entry) {
    breadcrumb(
      "routing.unknown_path",
      { path: normalizedPath, fallback: "not_found" },
      { category: "app" }
    );
    return notFoundResult(components, normalizedPath);
  }

  if (entry.featureFlag !== undefined) {
    if (featureFlagStatus === "pending") {
      return { kind: "pending" };
    }
    // A resolved `false` is an address this deployment does not serve to this caller, which is
    // what the not-found page says. No breadcrumb: the path is known, the feature is not held.
    if (featureFlags[entry.featureFlag] !== true) {
      return notFoundResult(components, normalizedPath);
    }
  }

  const context = {
    params,
    normalizedPath,
    compiledCatalog,
    versionIntent,
    entityTabSlug,
    baseEntityPath,
  };
  const mappedParams = entry.mapParams(context);
  if (!mappedParams) {
    return notFoundResult(components, normalizedPath);
  }

  if (mappedParams.notFound === true) {
    return notFoundResult(components, String(mappedParams.path ?? normalizedPath));
  }

  return {
    kind: "view",
    Component: entry.getComponent(components),
    params: mappedParams,
    remountKey: entry.remountKey?.(context) ?? normalizedPath,
  };
}
